/** Часовые пояса России (IANA): сроки, рабочие дни и даты в письмах считаются по поясу студии. */
export const RU_TIME_ZONES = [
  'Europe/Kaliningrad',
  'Europe/Moscow',
  'Europe/Samara',
  'Asia/Yekaterinburg',
  'Asia/Omsk',
  'Asia/Krasnoyarsk',
  'Asia/Irkutsk',
  'Asia/Yakutsk',
  'Asia/Vladivostok',
  'Asia/Magadan',
  'Asia/Kamchatka',
] as const;

export type RuTimeZone = (typeof RU_TIME_ZONES)[number];

/** Календарная дата `YYYY-MM-DD` в часовом поясе: «сегодня» студии не зависит от пояса сервера. */
export function isoDateIn(timeZone: string, at: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(at);
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

const clockFormats = new Map<string, Intl.DateTimeFormat>();

/** Местные дата и время по частям: форматтер на пояс создаётся один раз, он дорогой. */
function clockParts(
  timeZone: string,
  at: Date,
): { year: number; month: number; day: number; minutes: number; seconds: number } {
  let format = clockFormats.get(timeZone);
  if (format === undefined) {
    format = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    clockFormats.set(timeZone, format);
  }
  const parts = format.formatToParts(at);
  const value = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return {
    year: value('year'),
    month: value('month'),
    day: value('day'),
    minutes: (value('hour') % 24) * 60 + value('minute'),
    seconds: value('second'),
  };
}

/** Минуты от местной полуночи в часовом поясе: 9:30 → 570. */
export function minutesOfDayIn(timeZone: string, at: Date): number {
  return clockParts(timeZone, at).minutes;
}

/** На сколько местное время пояса опережает UTC в этот момент, миллисекунды. */
function zoneOffsetMs(timeZone: string, at: Date): number {
  const p = clockParts(timeZone, at);
  const local = Date.UTC(p.year, p.month - 1, p.day, 0, p.minutes, p.seconds);
  return local - Math.floor(at.getTime() / 1000) * 1000;
}

/**
 * Момент по местной дате и минутам от полуночи в часовом поясе. 1440 минут — конец дня (полночь
 * следующего). Пояс пересчитывается дважды, чтобы переход на другое смещение не сдвинул результат.
 */
export function zonedInstant(timeZone: string, date: string, minutesOfDay: number): Date {
  const [year = 0, month = 1, day = 1] = date.split('-').map(Number);
  const local = Date.UTC(year, month - 1, day, 0, minutesOfDay, 0);
  const first = local - zoneOffsetMs(timeZone, new Date(local));
  return new Date(local - zoneOffsetMs(timeZone, new Date(first)));
}
